import { useEffect, useState } from 'react';
import { ImageOff, Loader2 } from 'lucide-react';
import { useDb } from '../db/hooks';
import { useSync } from '../sync/SyncProvider';
import { loadAttachment } from '../sync/engine';
import type { Attachment } from '../data/tracking';

/** A stored photo. Photos added on another device are downloaded the first time they're shown. */
export function AttachmentImage({ attachment, className = '', alt }: { attachment: Attachment; className?: string; alt: string }) {
  const db = useDb();
  const userId = useSync()?.userId ?? null;
  const [state, setState] = useState<{ url: string } | 'loading' | 'missing'>('loading');

  useEffect(() => {
    let url: string | null = null;
    let cancelled = false;
    loadAttachment(db, userId, attachment.id)
      .then((bytes) => {
        if (cancelled) return;
        if (!bytes) {
          setState('missing');
          return;
        }
        url = URL.createObjectURL(new Blob([bytes], { type: attachment.mime }));
        setState({ url });
      })
      .catch(() => !cancelled && setState('missing'));
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [db, userId, attachment.id, attachment.mime]);

  if (state === 'loading' || state === 'missing') {
    return (
      <div className={`flex items-center justify-center bg-clinical-surface text-clinical-muted ${className}`} title={state === 'missing' ? 'Not on this device yet' : undefined}>
        {state === 'loading' ? <Loader2 className="h-5 w-5 animate-spin" /> : <ImageOff className="h-5 w-5" aria-label="Photo not available offline" />}
      </div>
    );
  }
  return <img src={state.url} alt={alt} className={`object-cover ${className}`} />;
}
